import { redirectToLogin as goToLogin } from '@/utils/authLoginRedirect'
import type { ErrorResponse } from '@repo/api-contracts'

/**
 * The error our `queryFn` / `mutationFn` wrappers throw when a ts-rest call
 * comes back with a non-2xx status. Carries the HTTP `status` so the global
 * `QueryCache.onError` handler in `queryClient.ts` can route 401s to the
 * login page (mirroring CCA's `redirectToLogin` flow).
 *
 * The constructor unwraps the BFF's standard `{ error: string }` error body
 * when present, falling back to the caller-supplied copy otherwise. This
 * means call sites just write:
 *
 *   throw new HttpError(status, body, 'Failed to update dog')
 */
export class HttpError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown, fallback: string) {
    super((body as ErrorResponse | undefined)?.error || fallback)
    this.name = 'HttpError'
    this.status = status
    this.body = body
  }
}

export function isUnauthorizedError(error: Error): boolean {
  return error instanceof HttpError && error.status === 401
}

let isRedirectingToLogin = false

/**
 * Redirects to the login page when an expired session is detected by a query.
 *
 * Note: `authenticatedFetch` in `src/utils/authenticatedFetch.ts` already
 * attempts a refresh before any 401 surfaces here, so this is a fallback for
 * when the refresh also fails.
 */
export function redirectToLogin(): void {
  if (isRedirectingToLogin) return
  isRedirectingToLogin = true
  goToLogin()
}
